import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/contexts";
import { getMyCompetitions, getPendingInvites } from "@/features/competitions/api";
import { getCurrentUserProfile } from "@/features/dashboard/api";
import { getAppleHealthConnectionStatus } from "@/features/sync/apple-health";
import { getGoogleHealthConnectionStatus } from "@/features/sync/google-health";
import { getHealthConnectConnectionStatus } from "@/features/sync/health-connect";
import { getCachedGet } from "@/lib/api";
import { isCompetitionEnded } from "@/server/helpers/general";
import type { CompetitionSummary, CompetitionsList, PendingInvite, UserProfile } from "@/types";

export function useDashboardData() {
  const { user, isLoading: authLoading } = useAuth();
  const [currentUser, setCurrentUser] = useState<UserProfile | null>(() =>
    getCachedGet<UserProfile>("/profiles/me"),
  );
  const [competitions, setCompetitions] = useState<CompetitionSummary[]>(
    () => getCachedGet<CompetitionsList>("/competitions")?.items ?? [],
  );
  const [pendingInvites, setPendingInvites] = useState<PendingInvite[]>(
    () => getCachedGet<PendingInvite[]>("/invites/pending") ?? [],
  );
  // A cache hit paints last session's data immediately; the fetch below
  // still runs to revalidate, but the skeleton only shows on a true cold load.
  const [loading, setLoading] = useState(() => getCachedGet("/competitions") === null);
  const [error, setError] = useState<string | null>(null);
  const [ghConnected, setGhConnected] = useState<boolean | null>(null);
  const [ahConnected, setAhConnected] = useState<boolean | null>(null);
  const [hcConnected, setHcConnected] = useState<boolean | null>(null);

  const fetchData = useCallback(async () => {
    setError(null);
    try {
      const [
        profileResult,
        competitionsResult,
        invitesResult,
        googleHealthResult,
        appleHealthResult,
        healthConnectResult,
      ] = await Promise.all([
        getCurrentUserProfile(),
        getMyCompetitions(),
        getPendingInvites(),
        getGoogleHealthConnectionStatus(),
        getAppleHealthConnectionStatus(),
        getHealthConnectConnectionStatus(),
      ]);
      if (profileResult.data) setCurrentUser(profileResult.data);
      if (competitionsResult.data) setCompetitions(competitionsResult.data.items);
      if (invitesResult.data) setPendingInvites(invitesResult.data);
      setGhConnected(googleHealthResult.connected);
      setAhConnected(appleHealthResult.connected);
      setHcConnected(healthConnectResult.connected);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading || !user) return;
    fetchData();
  }, [authLoading, user, fetchData]);

  const reload = useCallback(() => {
    setLoading(true);
    fetchData();
  }, [fetchData]);

  const userName = currentUser?.firstName ?? currentUser?.username ?? "User";
  const activeCompetitions = competitions.filter((c) => !isCompetitionEnded(c.endsAt, c.timezone));
  const pastCompetitions = competitions.filter((c) => isCompetitionEnded(c.endsAt, c.timezone));

  return {
    currentUser,
    competitions,
    pendingInvites,
    loading,
    error,
    ghConnected,
    ahConnected,
    hcConnected,
    userName,
    activeCompetitions,
    pastCompetitions,
    reload,
  };
}
